import { Box, Paper, Stack, Tab, Tabs, Typography } from '@mui/material'
import { AdCacheStatus } from '../../../component/shared'
import React, { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { TEXT } from '../../../utility/text'
import { Icon } from '../../../component/icon'
import { useEntities } from '../../../hooks'
import { EntitiesList } from '../../../component/admin/ad-management/entities'
import { EntityGroupsView } from '../../../component/admin/ad-management/entities/EntityGroupsView'

export enum EntitiesTypes {
  pharmacies = 'pharmacies',
  distributors = 'distributors',
  manufacturers = 'manufacturers',
  referral_types = 'referral_types',
  omit_measures = 'omit_measures',
  reports = 'reports',
}

export const getEntityTitle = (type: EntitiesTypes) => {
  switch (type) {
    case EntitiesTypes.pharmacies:
      return 'Pharmacy'
    case EntitiesTypes.distributors:
      return 'Distributor'
    // case EntitiesTypes.manufacturers:
    //   return 'Manufacturer'
    case EntitiesTypes.referral_types:
      return 'Referral Type'
    case EntitiesTypes.omit_measures:
      return 'Measure'
    case EntitiesTypes.reports:
      return 'Report'
    default:
      return ''
  }
}

interface ITabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function CustomTabPanel(props: Readonly<ITabPanelProps>) {
  const { children, value, index, ...other } = props

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ width: '100%' }}
      {...other}
    >
      {value === index && <Paper sx={{ p: 3 }}>{children}</Paper>}
    </div>
  )
}

const a11yProps = (index: number) => ({
  id: `entities-tab-${index}`,
  'aria-controls': `entities-tabpanel-${index}`,
})

export const EntitiesTabs = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const type = searchParams.get('type') as EntitiesTypes

  const getTabIndex = (type: EntitiesTypes) => {
    switch (type) {
      case EntitiesTypes.pharmacies:
        return 0
      case EntitiesTypes.distributors:
        return 1
      case EntitiesTypes.referral_types:
        return 2
      case EntitiesTypes.omit_measures:
        return 3
      case EntitiesTypes.reports:
        return 4
      // case EntitiesTypes.manufacturers:
      //   return 5
      default:
        return 0
    }
  }
  const [selectedTab, setSelectedTab] = useState(getTabIndex(type))
  const [entityType, setEntityType] = useState<EntitiesTypes>(EntitiesTypes.pharmacies)
  const [entityView, setEntityView] = useState<string>('')
  const [isEntitiesView, setIsEntitiesView] = useState(false)
  const [bgRefreshKey, setBgRefreshKey] = useState(0)
  const { isLoading, maxPageSize, page, totalCount, entities, setSearch, setPage, search, handleExportEntitiesButtonClick, getEntities } =
    useEntities(type)

  const getEntitiesWithBgRefresh = useCallback(async () => {
    setBgRefreshKey((k) => k + 1)
    await getEntities()
  }, [getEntities])

  useEffect(() => {
    switch (selectedTab) {
      case 0:
        setSearch('')
        setEntityType(EntitiesTypes.pharmacies)
        navigate('?type=pharmacies', { replace: true })
        break
      case 1:
        setSearch('')
        setEntityType(EntitiesTypes.distributors)
        navigate('?type=distributors', { replace: true })
        break
      case 2:
        setSearch('')
        setEntityType(EntitiesTypes.referral_types)
        navigate('?type=referral_types', { replace: true })
        break
      case 3:
        setSearch('')
        setEntityType(EntitiesTypes.omit_measures)
        navigate('?type=omit_measures', { replace: true })
        break
      case 4:
        setSearch('')
        setEntityType(EntitiesTypes.reports)
        navigate('?type=reports', { replace: true })
        break
      // case 5:
      //   setSearch('')
      //   setEntityType(EntitiesTypes.manufacturers)
      //   navigate('?type=manufecturers', { replace: true })
      //   break
    }
  }, [selectedTab, navigate, setSearch])

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
    setBgRefreshKey((k) => k + 1)
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 3 }}>
        <Typography
          variant='h1'
          data-testid='screen-title'
        >
          {TEXT.screen.entities.title}
        </Typography>
        <AdCacheStatus
          onRefresh={getEntities}
          refreshTrigger={bgRefreshKey}
        />
      </Box>

      <Box>
        <Tabs
          value={selectedTab}
          onChange={handleChange}
          data-testid='entities-list'
          aria-label='Entities List'
        >
          <Tab
            data-testid='pharmacy'
            aria-label='Pharmacy List'
            label={
              <Stack
                direction={'row'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Icon
                  name='Pharmacy'
                  stroke='CurrentColor'
                  strokeWidth='2'
                  fill='none'
                />
                <span>Pharmacies</span>
              </Stack>
            }
            {...a11yProps(0)}
          />
          <Tab
            data-testid='distributor'
            aria-label='Distributor List'
            label={
              <Stack
                direction={'row'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Icon
                  name='Distributors'
                  stroke='CurrentColor'
                  strokeWidth='2'
                  fill='none'
                />
                <span>Distributors</span>
              </Stack>
            }
            {...a11yProps(0)}
          />
          <Tab
            data-testid='referral-types'
            aria-label='Referral Types List'
            label={
              <Stack
                direction={'row'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Icon
                  name='ReferralTypes'
                  stroke='CurrentColor'
                  strokeWidth='2'
                  fill='none'
                />
                <span>Referral Types</span>
              </Stack>
            }
            {...a11yProps(0)}
          />
          <Tab
            data-testid='omit-measures'
            aria-label='Omit Measures List'
            label={
              <Stack
                direction={'row'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Icon
                  name='OmitMeasures'
                  stroke='CurrentColor'
                  strokeWidth='2'
                  fill='none'
                />
                <span>Omit Measures</span>
              </Stack>
            }
            {...a11yProps(0)}
          />
          <Tab
            data-testid='reports'
            aria-label='Reports List'
            label={
              <Stack
                direction={'row'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Icon
                  name='Reports'
                  stroke='CurrentColor'
                  strokeWidth='2'
                  fill='none'
                />
                <span>Reports</span>
              </Stack>
            }
            {...a11yProps(0)}
          />
          {/* <Tab
            data-testid='manufecturer'
            aria-label='Manufecturer List'
            label={
              <Stack
                direction={'row'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Icon
                  name='Pharmacy'
                  stroke='CurrentColor'
                  strokeWidth='2'
                  fill='none'
                />
                <span>Manufacturers</span>
              </Stack>
            }
            {...a11yProps(0)}
          /> */}
        </Tabs>
      </Box>
      <CustomTabPanel
        value={selectedTab}
        index={selectedTab}
      >
        <EntitiesList
          entities={entities}
          entityType={entityType}
          setSearch={setSearch}
          setEntity={setEntityView}
          totalCount={totalCount}
          page={page}
          maxPageSize={maxPageSize}
          setPage={setPage}
          setIsEntitiesView={setIsEntitiesView}
          isLoading={isLoading}
          search={search}
          handleExportEntitiesButtonClick={handleExportEntitiesButtonClick}
          getEntities={getEntitiesWithBgRefresh}
        />
      </CustomTabPanel>
      <EntityGroupsView
        type={type as EntitiesTypes}
        isShow={isEntitiesView}
        groupName={entityView}
        onCancel={() => setIsEntitiesView(false)}
      />
    </>
  )
}
